import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  History,
  ChevronRight,
  Calendar,
  GitBranch,
  Clock,
  Loader2,
  Archive,
  ArrowLeft,
} from 'lucide-react';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';
import { analytics } from '../services/analytics/posthog';
import { ExecutionBadge, HealthBadge, ProviderBadge } from './HistoryBadges';
import { HistoryFilterBar } from './HistoryFilterBar';
import { useAnalysisHistory } from '../hooks/useAnalysisHistory';
import type { AnalyzedRepository } from '../types/history';

const EmptyState: React.FC = () => {
  return (
    <div className="text-center py-16 px-6">
      <div className="max-w-md mx-auto">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-linear-to-br from-teal-500 to-blue-500 flex items-center justify-center">
          <History className="w-10 h-10 text-white" />
        </div>

        <h3 className="text-2xl font-semibold mb-3 text-gray-900 dark:text-white">
          No Analysis History Yet
        </h3>

        <p className="text-base leading-relaxed mb-8 text-gray-600 dark:text-gray-400">
          Start analyzing your repositories to see your history here. Each analysis will help you
          understand your code better and improve quality.
        </p>

        <Link
          to="/repositories"
          className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors duration-200 font-medium"
        >
          <GitBranch className="w-5 h-5" />
          Connect Your First Repository
        </Link>
      </div>
    </div>
  );
};

const RepositoryCard: React.FC<{ repo: AnalyzedRepository }> = ({ repo }) => (
  <Link
    to={`/repository/${repo.id}`}
    state={{ repository: repo, from: '/history' }}
    className="block bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 cursor-pointer transition-colors duration-200 hover:border-teal-500 focus:ring-2 focus:ring-teal-500 focus:outline-hidden no-underline text-inherit"
  >
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-3">
          {repo.provider === 'upload' ? (
            <Archive className="w-5 h-5 text-purple-500 shrink-0" />
          ) : (
            <GitBranch className="w-5 h-5 text-gray-500 shrink-0" />
          )}
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {repo.fullName || repo.name}
            </h3>
            {repo.provider === 'upload' && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                ID: {repo.id.slice(0, 8)}...
              </span>
            )}
          </div>
          <ProviderBadge provider={repo.provider} />
          <ExecutionBadge status={repo.executionStatus} />
          <HealthBadge health={repo.health} />
        </div>

        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
          {repo.lastAnalysis && (
            <div className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              <span>Last analyzed: {new Date(repo.lastAnalysis).toLocaleDateString()}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            <span>
              {repo.analysisCount} {repo.analysisCount === 1 ? 'analysis' : 'analyses'}
            </span>
          </div>
          {(repo.devScore !== null || repo.maintenanceScore !== null) && (
            <div className="flex items-center gap-3">
              {repo.devScore !== null && (
                <div className="flex items-center gap-1.5">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      repo.devScore >= 80
                        ? 'bg-green-500'
                        : repo.devScore >= 60
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                    }`}
                  />
                  <span>SDLC: {repo.devScore}/100</span>
                </div>
              )}
              {repo.maintenanceScore !== null && (
                <div className="flex items-center gap-1.5">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      repo.maintenanceScore >= 80
                        ? 'bg-green-500'
                        : repo.maintenanceScore >= 60
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                    }`}
                  />
                  <span>Drift: {repo.maintenanceScore}/100</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <ChevronRight className="w-6 h-6 text-gray-400 shrink-0 ml-4 self-center" />
    </div>
  </Link>
);

export const HistoryPage: React.FC = () => {
  const {
    loading,
    repositories,
    filteredRepositories,
    pagination,
    totalPages,
    searchQuery,
    filterExecutionStatus,
    sortBy,
    sortOrder,
    filterDateRange,
    debouncedSearch,
    setSearchQuery,
    setFilterExecutionStatus,
    setSortBy,
    setSortOrder,
    setFilterDateRange,
    handlePageChange,
  } = useAnalysisHistory();

  useEffect(() => {
    analytics.pageViewed('history');
  }, []);

  if (loading && repositories.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900">
        <Navbar pageTitle="Analysis History" showDivider={true} />
        <main className="flex-1 flex items-center justify-center pt-24">
          <div className="flex items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
            <span className="text-gray-600 dark:text-gray-400">
              Loading your analysis history...
            </span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900 px-4 sm:px-6 lg:px-8">
      <Navbar pageTitle="Analysis History" showDivider={true} />

      <main className="flex-1 max-w-7xl mx-auto py-8 pt-24 w-full">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/dashboard"
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Analysis History</h1>
              <p className="text-gray-600 dark:text-gray-400">
                View all your previous code analyses and their results
              </p>
            </div>
          </div>
        </div>

        {repositories.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <EmptyState />
          </div>
        ) : (
          <>
            <HistoryFilterBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              filterExecutionStatus={filterExecutionStatus}
              onFilterExecutionStatusChange={setFilterExecutionStatus}
              filterDateRange={filterDateRange}
              onFilterDateRangeChange={setFilterDateRange}
              sortBy={sortBy}
              onSortByChange={setSortBy}
              sortOrder={sortOrder}
              onSortOrderToggle={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
            />

            {/* Results Summary */}
            {filteredRepositories.length < repositories.length ? (
              <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                Showing {filteredRepositories.length} filtered{' '}
                {filteredRepositories.length === 1 ? 'result' : 'results'} on this page
                {debouncedSearch && ` matching "${debouncedSearch}"`}
              </div>
            ) : pagination.total > 0 ? (
              <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} {pagination.total === 1 ? 'repository' : 'repositories'}
                {debouncedSearch && ` matching "${debouncedSearch}"`}
              </div>
            ) : null}

            {/* Repository List */}
            <div className="space-y-4">
              {filteredRepositories.map((repo) => (
                <RepositoryCard key={repo.id} repo={repo} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    return (
                      <button
                        key={`page-${pageNum}`}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                          pageNum === pagination.page
                            ? 'bg-teal-600 text-white'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === totalPages}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};
